import { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { InputField } from "../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../components/input-button/InputButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import IconButton from "@material-ui/core/IconButton";
import BusinessWithdrawPercent from "../../../../../models/BusinessWithdrawPercent";
import BusinessWithdrawPercentService from "../../../../../services/BusinessWithdrawPercentService";
import BusinessService from "../../../../../services/BusinessService";

interface EditWithdrawTarificationProps {
  businesses: any;
  percentage: BusinessWithdrawPercent;
  closeCallback: () => void;
  submitCallback: () => void;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
  },
  modalBody: {
    width: "90%",
    padding: 10,
    border: "none",
  },

  antPickerDropdown: {
    zIndex: 100000,
  },
}));

export const EditBusinessWithdrawPercent = (
  props: EditWithdrawTarificationProps
) => {
  const classes = useStyles();
  const intl = useIntl();
  const { formatMessage } = useIntl();
  const { percentage, closeCallback } = props;
  const { submitCallback } = props;
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [succeed, setSucceed] = useState(false);
  const [businesses, setBusinesses] = useState(props.businesses);
  const [currentPercent, setCurrentPercent] = useState(props.percentage);
  const [currentBusiness, setCurrentBusiness] = useState([props.percentage.business])

  const formik = useFormik({
    initialValues: {
      commission_percentage: "",
      business_id: "",
    },
    onSubmit: async (
      values,
      { setSubmitting, resetForm }: FormikHelpers<any>
    ) => {
      setError(false);
      try {
        const addTarificationResult = await BusinessWithdrawPercentService.updateWithdrawPercent(
          currentPercent.id,
          currentPercent
        );
        if (
          addTarificationResult.isSuccess &&
          addTarificationResult.data.code === 100
        ) {
          resetForm({});
          setSucceed(true);
          setTimeout(() => {
            setSucceed(false);
          }, 2000);
          submitCallback();
        } else {
          setError(true);
          if (addTarificationResult.data.code === 101) {
            let errorMessage = formatMessage({
              id: "sharedScreen.thisBusinessAlreadyHaveAPercent",
            });
            setErrorMessage(errorMessage);
          } else {
            setErrorMessage("");
          }
          setTimeout(() => {
            setError(false);
          }, 5000);
        }
      } catch (error) {
        setError(true);
      }
      setSubmitting(false);
    },
  });

  const handleDataChange = (name: string, value: string) => {
    let currPercent = { ...currentPercent };
    currPercent[name] = value;

    setCurrentPercent(currPercent);
  };

  const loadBusinesses = () => {
    const response = BusinessService.getAllbusinesses();

    response.then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let businesses = JSON.parse(JSON.stringify(response.data.data));

        setBusinesses(businesses);
      } else {
        setBusinesses(businesses);
      }
    });
  };

  useEffect(() => {
    setCurrentPercent(props.percentage);
    setCurrentBusiness([props.percentage.business]);
    loadBusinesses();
    //setTabData(tab);
  }, [percentage]);

  return (
    <>
      <Paper className={classes.modalBody}>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              xs={12}
              sm={12}
              lg={12}
            >
              <Typography
                className={classes.modalTitle}
                variant="h4"
                component="h4"
              >
                <FormattedMessage id="sharedScreen.editWithdrawPercent" />
              </Typography>
              <IconButton edge="end" onClick={closeCallback}>
                <HighlightOffIcon fontSize="large" />
              </IconButton>
            </Grid>
            {error && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="error">
                  {errorMessage === "" && (
                    <FormattedMessage id="sharedScreen.thereWasAProblemEditingBusinesswithdrawPercent" />
                  )}
                  {errorMessage !== "" && <p>{errorMessage}</p>}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={12} lg={12}>
              <TextField
                select
                variant="outlined"
                size="small"
                value={currentPercent.business.id}
                label={formatMessage({ id: "sharedScreen.business" })}
                fullWidth
                name="business_id"
                onChange={(event) =>
                  handleDataChange(event.target.name, event.target.value)
                }
                error={
                  formik.touched.business_id &&
                  Boolean(formik.errors.business_id)
                }
                helperText={
                  formik.touched.business_id && formik.errors.business_id
                }
              >
                {currentBusiness.map((business) => (
                  <MenuItem key={business.id} value={business.id}>
                    {business.company_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                value={currentPercent.commission_percentage}
                type="number"
                fullWidth
                placeholder={formatMessage({
                  id: "sharedScreen.commissionPercentage",
                })}
                name="commission_percentage"
                onChange={(event) =>
                  handleDataChange(event.target.name, event.target.value)
                }
                error={
                  formik.touched.commission_percentage &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description &&
                  formik.errors.commission_percentage
                }
              />
            </Grid>
            <br />
            <br />

            <Grid item xs={12} sm={12} lg={12} container direction="row">
              <InputButton
                disabled={formik.isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
              >
                <FormattedMessage id="sharedScreen.edit" />
                {formik.isSubmitting && <CircularProgress size={30} />}
              </InputButton>
            </Grid>
            {succeed && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="success">
                  <FormattedMessage id="sharedScreen.withdrawPercentUpdatedSuccessfully" />
                </Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </>
  );
};
